/**
 * Admin Operations API service.
 *
 * Wraps the operational/management endpoints exposed by the Functions backend
 * that power the Admin Portal. The two headline pipeline jobs are:
 *
 *   - "Refresh Event Sources with WebIQ"  -> refreshAndProcessEventSources
 *   - "Extract Events from Web Sources with AI" -> startQueueBasedProcessing
 *
 * The endpoint names are kept as-is because scheduled automation and saved
 * requests depend on them; only the display names changed.
 *
 * Supporting endpoints:
 *
 *   - getSystemInsights
 *   - diagnoseSyncStatus
 *   - syncSearchIndex
 *   - cleanupOrphanedDocs
 *
 * These calls can each take from a few seconds to several minutes. The client
 * timeout is set generously (15 minutes) per the UX requirements.
 */
import config from '../config';
import type { AppMetadata } from '../types/api';
import { APP_SERVICE_SKU_RATES } from './azurePricing';

// 15 minutes — these endpoints can be very long-running.
const ADMIN_TIMEOUT_MS = 15 * 60 * 1000;

function adminUrl(path: string, qs?: Record<string, unknown>): string {
  const base = `${config.api.baseUrl}/${path}`;
  if (!qs) return base;
  const params = new URLSearchParams();
  Object.entries(qs).forEach(([k, v]) => {
    if (v !== undefined && v !== null && v !== '') {
      params.append(k, String(v));
    }
  });
  const query = params.toString();
  return query ? `${base}?${query}` : base;
}

async function adminFetch<T>(
  url: string,
  init: RequestInit = {}
): Promise<{ data: T; status: number; durationMs: number }> {
  const start = performance.now();
  const response = await fetch(url, {
    ...init,
    headers: {
      'Content-Type': 'application/json',
      ...(init.headers ?? {}),
    },
    signal: AbortSignal.timeout(ADMIN_TIMEOUT_MS),
  });

  const durationMs = Math.round(performance.now() - start);

  // Many of these endpoints intentionally return 207 for partial success.
  // We treat 2xx as success and let callers inspect `errors[]` in the body.
  let body: any = null;
  try {
    body = await response.json();
  } catch {
    // ignore — body may be empty
  }

  if (!response.ok) {
    const message =
      (body && (body.message || body.error)) ||
      `HTTP ${response.status} ${response.statusText}`;
    const err = new Error(message) as Error & { status?: number; body?: any };
    err.status = response.status;
    err.body = body;
    throw err;
  }

  return { data: body as T, status: response.status, durationMs };
}

// ---------------------------------------------------------------------------
// AI decisions audit and correction
// ---------------------------------------------------------------------------

export type AiDecisionType = 'source' | 'relationship' | 'event' | 'logo' | 'audit';

export interface AiDecisionFilters {
  decisionType: AiDecisionType;
  page?: number;
  limit?: number;
  entityId?: number | string;
  eventId?: number | string;
  sourceId?: number | string;
  status?: string;
  role?: string;
  minConfidence?: number | string;
  maxConfidence?: number | string;
  dateFrom?: string;
  dateTo?: string;
}

export interface AiDecisionListResponse {
  decisionType: AiDecisionType;
  page: number;
  limit: number;
  total: number;
  items: Array<Record<string, any>>;
}

export interface AiDecisionAction {
  action:
    | 'set_event_relevance_override'
    | 'clear_event_relevance_override'
    | 'set_entity_event_override'
    | 'correct_relationship'
    | 'correct_source_relationship'
    | 'reset_relationship_correction'
    | 'reset_source_relationship_correction'
    | 'select_logo'
    | 'remove_logo'
    | 'reset_logo_selection'
    | 'rescore'
    | 'retry';
  reason?: string;
  decisionType?: AiDecisionType;
  entityId?: number;
  eventInfoId?: number;
  eventSourceId?: number;
  assetId?: number;
  relationshipType?: string;
  decisionStatus?: 'confirmed' | 'unlikely';
  relevanceStatus?: 'confirmed' | 'unlikely';
  forcedStatus?: 'RELEVANT' | 'EARNINGS_CALL';
  isLinked?: boolean;
}

export async function getAiDecisions(
  filters: AiDecisionFilters
): Promise<AiDecisionListResponse> {
  const { data } = await adminFetch<AiDecisionListResponse>(
    adminUrl('ai-decisions', filters as unknown as Record<string, unknown>)
  );
  return data;
}

export async function applyAiDecisionAction(
  action: AiDecisionAction
): Promise<Record<string, any>> {
  const { data } = await adminFetch<Record<string, any>>(
    adminUrl('ai-decisions'),
    { method: 'POST', body: JSON.stringify(action) }
  );
  return data;
}

// ---------------------------------------------------------------------------
// refreshAndProcessEventSources (WebIQ discovery)
// ---------------------------------------------------------------------------

export interface WebIqDiscoverySettings {
  entityTypes: string[];
  entityIds: number[] | null;
  maxResults: number;
  language: string;
  region: string;
  pageSize: number;
}

export interface WebIqDiscoveryStartResponse {
  processId: string;
  status: 'queued';
  statusUrl: string;
  settings: WebIqDiscoverySettings;
}

export interface WebIqDiscoveryStatus {
  processId: string;
  status:
    | 'queued'
    | 'dispatching'
    | 'dispatched'
    | 'completed'
    | 'completed_with_errors'
    | 'failed';
  settings: WebIqDiscoverySettings;
  dispatch: {
    entitiesQueued: number;
    startedAt?: string | null;
    completedAt?: string | null;
    error?: string | null;
  };
  runs: {
    total: number;
    queued: number;
    searching: number;
    noResults: number;
    sourcesActivated: number;
    completed: number;
    failed: number;
    superseded: number;
    acceptedSources: number;
  };
  createdAt?: string | null;
  completedAt?: string | null;
  errors: Array<{
    RunId?: string;
    EntityId?: number;
    ErrorCode?: string;
    ErrorMessage?: string;
    CompletedAt?: string | null;
  }>;
}

export async function startWebIqDiscovery(input: {
  entityId: number;
  entityType: string;
  maxResults?: number;
  language?: string;
  region?: string;
}): Promise<WebIqDiscoveryStartResponse> {
  const url = adminUrl('refreshAndProcessEventSources');
  const { data } = await adminFetch<WebIqDiscoveryStartResponse>(url, {
    method: 'POST',
    body: JSON.stringify({
      entityIds: [input.entityId],
      entityTypes: [input.entityType],
      maxResults: input.maxResults ?? 10,
      language: input.language ?? 'en',
      region: input.region ?? 'US',
      pageSize: 1,
    }),
  });
  return data;
}

export async function getWebIqDiscoveryStatus(
  processId: string
): Promise<WebIqDiscoveryStatus> {
  const url = adminUrl('refreshAndProcessEventSources', { processId });
  const { data } = await adminFetch<WebIqDiscoveryStatus>(url, {
    method: 'GET',
  });
  return data;
}

export interface EventSourceRefreshParams {
  entityTypes?: string[];
  entityIds?: number[] | null;
  maxResults?: number;
  language?: string;
  region?: string;
  pageSize?: number;
}

/** Starts "Refresh Event Sources with WebIQ" for a whole scope, not one entity. */
export async function startEventSourceRefresh(
  params: EventSourceRefreshParams
): Promise<WebIqDiscoveryStartResponse> {
  const body: Record<string, unknown> = {};
  if (params.entityTypes && params.entityTypes.length > 0) {
    body.entityTypes = params.entityTypes;
  }
  if (params.entityIds && params.entityIds.length > 0) {
    body.entityIds = params.entityIds;
  }
  if (params.maxResults != null) body.maxResults = params.maxResults;
  if (params.language) body.language = params.language;
  if (params.region) body.region = params.region;
  if (params.pageSize != null) body.pageSize = params.pageSize;

  const url = adminUrl('refreshAndProcessEventSources');
  const { data } = await adminFetch<WebIqDiscoveryStartResponse>(url, {
    method: 'POST',
    body: JSON.stringify(body),
  });
  return data;
}

// ---------------------------------------------------------------------------
// startQueueBasedProcessing — "Extract Events from Web Sources with AI"
// ---------------------------------------------------------------------------

export interface EventExtractionParams {
  batchSize?: number;
  offset?: number;
  entityId?: number;
  entityTypes?: string[];
  eventSourceId?: number;
  eventSourceTypeId?: number;
  processAllBatches?: boolean;
  // Bypasses unchanged/time-window decisions but still records a gate run.
  forceExtraction?: boolean;
  // Used by nightly automation to inspect candidate count before scale-up.
  deferDispatch?: boolean;
}

export interface EventExtractionResponse {
  // The original response fields remain required for compatibility with gate
  // mode off. Gate-enabled 202/status responses add the optional fields below.
  success: boolean;
  message?: string;
  enqueuedCount: number;
  errorCount: number;
  totalRecordsInBatch?: number;
  totalRecords?: number;
  batchSize: number;
  offset?: number;
  hasMoreBatches?: boolean;
  nextOffset?: number;
  batchesProcessed?: number;
  processId?: string;
  processingTime: number;
  errors: Array<{ eventSourceId?: number; message: string } | string>;
  description?: string;
  error?: string;
  gateEnabled?: boolean;
  status?: 'queued' | 'probing' | 'ready' | 'dispatching' | 'dispatched' | 'completed' | 'completed_with_errors' | 'failed' | string;
  probe?: {
    complete: boolean;
    sourcesQueued: number;
    startedAt?: string | null;
    sourceEnumerationCompletedAt?: string | null;
    completedAt?: string | null;
  };
  runs?: {
    total: number;
    queued: number;
    probing: number;
    skipped: number;
    candidates: number;
    dispatching: number;
    dispatched: number;
    completed: number;
    failed: number;
    superseded: number;
    unchanged: number;
    changed: number;
    periodic: number;
    probeFailed: number;
    unusableText: number;
  };
  dispatch?: {
    startedAt?: string | null;
    completedAt?: string | null;
  };
}

export async function startQueueBasedProcessing(
  params: EventExtractionParams
): Promise<EventExtractionResponse> {
  // The Function reads these from the query string, not the JSON body.
  // Sending them in the body causes offset/batchSize to be ignored, which
  // makes every call return the same first page of records.
  const url = adminUrl('startQueueBasedProcessing', {
    batchSize: params.batchSize,
    offset: params.offset,
    entityId: params.entityId,
    entityTypes: params.entityTypes?.join(','),
    eventSourceId: params.eventSourceId,
    eventSourceTypeId: params.eventSourceTypeId,
    processAllBatches: params.processAllBatches,
    forceExtraction: params.forceExtraction,
    deferDispatch: params.deferDispatch,
  });
  const { data } = await adminFetch<EventExtractionResponse>(url, {
    method: 'POST',
  });
  return data;
}

export async function getEventExtractionStatus(
  processId: string
): Promise<EventExtractionResponse> {
  // Status polling is read-only; heavy dispatch is a separate private backend
  // action used by nightly automation after conditional scale-up.
  const url = adminUrl('startQueueBasedProcessing', { processId });
  const { data } = await adminFetch<EventExtractionResponse>(url, { method: 'GET' });
  return data;
}

export interface GateMethodPeriod {
  totalRuns: number;
  measuredMethodRuns: number;
  screenshotRuns: number;
  screenshotRatePct: number | null;
  markdownRuns: number;
  jinaTextRuns: number;
  localDomTextRuns: number;
  totalTokens: number;
  estimatedCostUsd: number;
}

export interface ExtractionGateImpactResponse {
  generatedAt: string;
  filters: { days: number; entityId: number | null; since: string | null };
  rollout: {
    gateMode: 'off' | 'shadow' | 'enforce';
    normalizationVersion: string;
    firstGateRunAt: string | null;
    latestGateRunAt: string | null;
    maturity: 'not_started' | 'baselines_only' | 'repeat_measurements';
  };
  savings: {
    totalGateRuns: number;
    completedProbeRuns: number;
    currentNormalizationRuns: number;
    pendingProbeRuns: number;
    pendingHeavyRuns: number;
    uniqueSourcesProbed: number;
    repeatSources: number;
    repeatProbeRuns: number;
    measuredDecisionRuns: number;
    avoidableAiRuns: number;
    potentialAiReductionPct: number | null;
    shadowWouldSkipRuns: number;
    actuallySkippedRuns: number;
    firstBaselineRuns: number;
    legacyOpaqueShadowRuns: number;
    changedRuns: number;
    periodicRefreshRuns: number;
    failedRuns: number;
    completedRuns: number;
  };
  jina: {
    usableRuns: number;
    probeFailedRuns: number;
    unusableTextRuns: number;
    usableRatePct: number | null;
    averageQualityScore: number | null;
  };
  coverage: {
    activeWebsiteSources: number;
    baselinedSources: number;
    baselineCoveragePct: number | null;
    pendingBaselineSources: number;
    qualifiedSources: number;
    qualifiedPct: number | null;
    weeklyRecheckSources: number;
    canWaitAtLeast: {
      oneWeek: number;
      twoWeeks: number;
      oneMonth: number;
      twoMonths: number;
      threeMonths: number;
    };
    upcomingEventsRepresented: number;
    furthestCoverageThroughAt: string | null;
    scheduleBuckets: Array<{ bucket: string; sourceCount: number }>;
    policy: {
      minimumUpcomingEvents: number;
      horizonDays: number;
      leadDays: number;
      recheckDays: number;
    };
  };
  screenshots: {
    beforeJina: GateMethodPeriod;
    withJina: GateMethodPeriod;
    comparisonReady: boolean;
    absoluteReductionPoints: number | null;
    relativeReductionPct: number | null;
    fallbackReasons: Array<{ reason: string; runCount: number }>;
  };
  decisions: Array<{ decision: string; runCount: number }>;
  recentBatches: Array<{
    batchId: string;
    status: string;
    gateMode: string;
    createdAt: string;
    completedAt: string | null;
    totalRuns: number;
    avoidableAiRuns: number;
    textFallbackRuns: number;
    failedRuns: number;
  }>;
}

export async function getExtractionGateImpact(params: {
  days?: number;
  entityId?: number;
} = {}): Promise<ExtractionGateImpactResponse> {
  const url = adminUrl('catalog/extractionGateAnalytics', params);
  const { data } = await adminFetch<ExtractionGateImpactResponse>(url, {
    method: 'GET',
  });
  return data;
}

// ---------------------------------------------------------------------------
// Extraction AI Batch operations
// ---------------------------------------------------------------------------

export interface ExtractionBatchDiagnostic {
  code: string;
  severity: 'error' | 'warning';
  message: string;
  count: number | null;
}

export interface ExtractionBatchUsage {
  inputTokens: number;
  cachedInputTokens: number;
  outputTokens: number;
  reasoningTokens?: number;
  totalTokens: number;
}

export interface ExtractionBatchCost {
  batchUsd: number | null;
  standardEquivalentUsd: number | null;
  savingsUsd: number | null;
  savingsPct: number | null;
}

export interface ExtractionBatchTiming {
  buildMs: number | null;
  providerMs: number | null;
  importMs: number | null;
  totalMs: number | null;
  fallbackAt: string | null;
  outerDeadlineAt: string | null;
  submittedAt: string | null;
  providerInProgressAt: string | null;
  providerCompletedAt: string | null;
  cancelRequestedAt: string | null;
  importedAt: string | null;
  createdAt: string | null;
  modifiedAt: string | null;
}

export interface ExtractionBatchRow {
  id: string;
  gateBatchId: string;
  chunkIndex: number;
  status: string;
  providerBatchId: string | null;
  inputFileId: string | null;
  outputFileId: string | null;
  errorFileId: string | null;
  deployment: string | null;
  model: string | null;
  modelVersion: string | null;
  deploymentSku: string | null;
  apiVersion: string | null;
  inputHash: string | null;
  itemCount: number;
  persistedItemCount: number;
  completedCount: number;
  failedCount: number;
  pendingItemCount: number;
  succeededItemCount: number;
  failedItemCount: number;
  fallbackItemCount: number;
  lateIgnoredItemCount: number;
  providerSucceededCount: number;
  providerFailedCount: number;
  providerMissingCount: number;
  inputBytes: number | null;
  usage: ExtractionBatchUsage;
  cost: ExtractionBatchCost;
  timing: ExtractionBatchTiming;
  error: { code: string | null; message: string | null } | null;
  diagnostics: ExtractionBatchDiagnostic[];
  terminal: boolean;
}

export interface ExtractionBatchItem {
  id: string;
  batchId: string;
  gateRunId: string;
  eventSourceId: number | null;
  sourceUrl: string | null;
  entityId: number | null;
  entityName: string | null;
  workflowMetadataId: number | null;
  workflowStatus: string | null;
  customId: string | null;
  status: string;
  workflowAttemptCount: number;
  providerResultStatus: string | null;
  responseStatusCode: number | null;
  providerRequestId: string | null;
  servedModel: string | null;
  finishReason: string | null;
  inputArtifactId: number;
  resultArtifactId: number | null;
  usage: ExtractionBatchUsage;
  estimatedBatchCostUsd: number | null;
  timing: {
    submittedAt: string | null;
    resultReceivedAt: string | null;
    continuationQueuedAt: string | null;
    fallbackQueuedAt: string | null;
    completedAt: string | null;
    createdAt: string | null;
    modifiedAt: string | null;
    workflowStartedAt: string | null;
    workflowCompletedAt: string | null;
  };
  error: { code: string | null; message: string | null } | null;
  diagnostics: ExtractionBatchDiagnostic[];
}

export interface ExtractionBatchAnalyticsResponse {
  generatedAt: string;
  schemaReady: boolean;
  filters: GetExtractionBatchAnalyticsParams;
  rollout: {
    mode: 'off' | 'canary' | 'enforce';
    enabled: boolean;
    canaryPercent: number;
    deployment: string | null;
  };
  summary: {
    since: string | null;
    totalBatches: number;
    totalItems: number;
    completedItems: number;
    failedItems: number;
    fallbackItems: number;
    lateIgnoredItems: number;
    issueBatches: number;
    inputBytes: number;
    usage: ExtractionBatchUsage;
    providerDuration: {
      averageMs: number | null;
      p50Ms: number | null;
      p95Ms: number | null;
      maximumMs: number | null;
    };
    cost: ExtractionBatchCost & { pricingComplete: boolean };
  } | null;
  statusCounts: Array<{ status: string; count: number }>;
  batches: ExtractionBatchRow[];
  page: number;
  pageSize: number;
  totalCount: number;
  totalPages: number;
  selectedBatch: ExtractionBatchRow | null;
  items: ExtractionBatchItem[];
  itemPage: number;
  itemPageSize: number;
  itemTotalCount: number;
  itemTotalPages: number;
  diagnostics: ExtractionBatchDiagnostic[];
  pricing: { note: string; priceUrl: string };
}

export interface GetExtractionBatchAnalyticsParams {
  days?: number;
  page?: number;
  pageSize?: number;
  status?: string;
  issuesOnly?: boolean;
  search?: string;
  batchId?: string;
  itemPage?: number;
  itemPageSize?: number;
}

export async function getExtractionBatchAnalytics(
  params: GetExtractionBatchAnalyticsParams = {},
): Promise<ExtractionBatchAnalyticsResponse> {
  const { data } = await adminFetch<ExtractionBatchAnalyticsResponse>(
    adminUrl('catalog/extractionBatchAnalytics', params as Record<string, unknown>),
    { method: 'GET' },
  );
  return data;
}

// ---------------------------------------------------------------------------
// Local human verification for extraction Batch quality
// ---------------------------------------------------------------------------

export interface BatchReviewEventValues {
  title: string | null;
  startDate: string | null;
  endDate: string | null;
  type: string | null;
  venue: string | null;
  city: string | null;
  state: string | null;
  country: string | null;
  registrationUrl: string | null;
  eventDetailUrl: string | null;
}

export interface BatchReviewEvent {
  goldEventId: string;
  origin: 'source_parser' | 'human_added';
  status: 'pending' | 'confirmed' | 'rejected';
  values: BatchReviewEventValues;
  sourceEvidence: string;
  note: string;
}

export interface BatchReviewCandidateState {
  candidateId: string;
  status: 'pending' | 'confirmed';
  decision: 'event' | 'not_event';
  goldEventId: string | null;
  note: string;
}

export interface BatchReviewCandidate {
  candidateId: string;
  decision: 'event' | 'not_event';
  goldEventId: string | null;
  suggestedGoldEventId: string;
  event: BatchReviewEventValues;
  observationCount: number;
  notes: string;
  review: BatchReviewCandidateState;
}

export interface BatchReviewProgress {
  eventsReviewed: number;
  eventsTotal: number;
  candidatesReviewed: number;
  candidatesTotal: number;
  reviewed: number;
  total: number;
  complete: boolean;
}

export interface BatchReviewCaseSummary {
  caseId: string;
  entityName: string | null;
  url: string | null;
  manifestDuplicate: boolean;
  progress: BatchReviewProgress;
}

export interface BatchReviewSummary {
  reviewer: string;
  startedAt: string;
  updatedAt: string;
  finalizedAt: string | null;
  reviewed: number;
  total: number;
  remaining: number;
  complete: boolean;
  cases: BatchReviewCaseSummary[];
}

export interface BatchReviewCase {
  caseId: string;
  entityName: string | null;
  url: string | null;
  manifestEntityNames: string[];
  manifestDuplicate: boolean;
  sourceMarkdown: string;
  events: BatchReviewEvent[];
  candidates: BatchReviewCandidate[];
  progress: BatchReviewProgress;
}

export interface BatchReviewWorkspaceResponse {
  summary: BatchReviewSummary;
  case: BatchReviewCase | null;
}

export interface BatchReviewFinalizeResponse {
  summary: BatchReviewSummary;
  result: {
    labelsPath: string;
    scorePath: string;
    score: {
      pairedRuns: number;
      economics: {
        savingsPct: number | null;
        standardCostUsd: number | null;
        batchCostUsd: number | null;
      };
      nonInferiority: Record<string, {
        standard: number | null;
        batch: number | null;
        difference: number | null;
        lowerOneSided95: number | null;
        margin: number;
        passes: boolean;
      }>;
      gate: {
        passes: boolean;
        independentHumanReview: boolean;
        materialInferenceSavings: boolean;
        noInvalidRateIncrease: boolean;
        noCatastrophicZeroRateIncrease: boolean;
      };
    };
  };
}

function localBatchReviewUrl(path = '', qs?: Record<string, unknown>): string {
  const base = `/api/local/extractionBatchReview${path}`;
  if (!qs) return base;
  const params = new URLSearchParams();
  Object.entries(qs).forEach(([key, value]) => {
    if (value !== undefined && value !== null && value !== '') params.set(key, String(value));
  });
  return params.size ? `${base}?${params}` : base;
}

export async function getExtractionBatchReview(
  caseId?: string,
): Promise<BatchReviewWorkspaceResponse> {
  const { data } = await adminFetch<BatchReviewWorkspaceResponse>(
    localBatchReviewUrl('', { caseId }),
    { method: 'GET' },
  );
  return data;
}

export async function saveExtractionBatchReviewer(
  reviewer: string,
): Promise<BatchReviewWorkspaceResponse> {
  const { data } = await adminFetch<BatchReviewWorkspaceResponse>(
    localBatchReviewUrl('/reviewer'),
    { method: 'PUT', body: JSON.stringify({ reviewer }) },
  );
  return data;
}

export async function saveExtractionBatchReviewCase(input: {
  caseId: string;
  reviewer: string;
  events: BatchReviewEvent[];
  candidates: BatchReviewCandidateState[];
}): Promise<BatchReviewWorkspaceResponse> {
  const { data } = await adminFetch<BatchReviewWorkspaceResponse>(
    localBatchReviewUrl(`/cases/${encodeURIComponent(input.caseId)}`),
    {
      method: 'PUT',
      body: JSON.stringify({
        reviewer: input.reviewer,
        events: input.events,
        candidates: input.candidates,
      }),
    },
  );
  return data;
}

export async function finalizeExtractionBatchReview(): Promise<BatchReviewFinalizeResponse> {
  const { data } = await adminFetch<BatchReviewFinalizeResponse>(
    localBatchReviewUrl('/finalize'),
    { method: 'POST', body: '{}' },
  );
  return data;
}

// ---------------------------------------------------------------------------
// getSystemInsights
// ---------------------------------------------------------------------------

export interface AdminSystemInsightsParams {
  timeframe?: number; // days
  includeDetails?: boolean;
}

// The shape is documented loosely; pages dig into specific paths and tolerate
// missing sub-objects. Use a permissive type here.
export type AdminSystemInsightsResponse = any;

export async function getAdminSystemInsights(
  params: AdminSystemInsightsParams = {}
): Promise<AdminSystemInsightsResponse> {
  const url = adminUrl('getSystemInsights', {
    timeframe: params.timeframe,
    includeDetails: params.includeDetails,
  });
  const { data } = await adminFetch<AdminSystemInsightsResponse>(url, {
    method: 'GET',
  });
  return data;
}

// ---------------------------------------------------------------------------
// diagnoseSyncStatus
// ---------------------------------------------------------------------------

export interface DiagnoseSyncResponse {
  overview: {
    databaseEventCount: number;
    searchDocumentCount: number;
    inSync: boolean;
    difference: number;
  };
  missingFromSearch: {
    count: number;
    sampleEvents?: Array<{
      id: number;
      title?: string;
      startDate?: string;
      dateCreated?: string;
    }>;
    byCreationDate?: Record<string, number>;
  };
  orphanedInSearch: {
    count: number;
    sampleDocs?: Array<{
      searchId: string;
      databaseEventId?: number;
      title?: string;
      startDate?: string;
    }>;
  };
  documentsWithoutDbId: {
    count: number;
    note?: string;
  };
  recommendations: Array<{
    action: 'add_missing' | 'remove_orphans' | 'full_sync' | 'none' | string;
    message: string;
    endpoint?: string;
  }>;
}

export async function diagnoseSyncStatus(): Promise<DiagnoseSyncResponse> {
  const url = adminUrl('diagnoseSyncStatus');
  const { data } = await adminFetch<DiagnoseSyncResponse>(url, { method: 'GET' });
  return data;
}

// ---------------------------------------------------------------------------
// syncSearchIndex
// ---------------------------------------------------------------------------

export interface SyncSearchIndexOptions {
  dryRun?: boolean;
  skipCleanup?: boolean;
  skipAdd?: boolean;
}

export interface SyncSearchIndexResponse {
  initial: { databaseCount: number; searchCount: number; difference: number };
  options: { dryRun: boolean; skipCleanup: boolean; skipAdd: boolean };
  cleanup: {
    orphanedCount: number;
    deletedCount: number;
    sampleOrphans?: Array<any>;
    errors: Array<{ message: string; [k: string]: any }>;
  };
  addition: {
    missingCount: number;
    addedCount: number;
    sampleMissing?: Array<any>;
    errors: Array<{ message: string; [k: string]: any }>;
  };
  summary: {
    expectedFinalCount: number;
    inSync: boolean;
    message: string;
    durationMs?: number;
  };
}

export async function syncSearchIndex(
  options: SyncSearchIndexOptions
): Promise<{ data: SyncSearchIndexResponse; status: number }> {
  const url = adminUrl('syncSearchIndex');
  const { data, status } = await adminFetch<SyncSearchIndexResponse>(url, {
    method: 'POST',
    body: JSON.stringify(options),
  });
  return { data, status };
}

// ---------------------------------------------------------------------------
// cleanupOrphanedDocs
// ---------------------------------------------------------------------------

export interface OrphanedDocSample {
  searchId: string;
  databaseEventId?: number;
  title?: string;
  startDate?: string;
}

export interface CleanupOrphansResponse {
  totalSearchDocuments: number;
  validDatabaseIds: number;
  orphanedDocuments: number;
  documentsWithoutDbId?: number;
  /** Combined count of stale + missing-dbId docs that will be (or were) removed. */
  totalToRemove?: number;
  /** True when GET (scan); false when DELETE actually removed documents. */
  dryRun?: boolean;
  /** New name returned by the backend; older builds returned `sampleOrphans`. */
  sampleOrphanedDocs?: OrphanedDocSample[];
  /** Sample of search docs that lack a `databaseEventId` field altogether. */
  sampleDocsWithoutDbId?: OrphanedDocSample[];
  /** @deprecated Older field name, kept for backward compatibility. */
  sampleOrphans?: OrphanedDocSample[];
  deletedCount?: number;
  failedCount?: number;
  errors?: Array<{ message: string; [k: string]: any }>;
  durationMs?: number;
  message: string;
}

export async function scanOrphanedDocs(): Promise<{
  data: CleanupOrphansResponse;
  status: number;
}> {
  const url = adminUrl('cleanupOrphanedDocs');
  const { data, status } = await adminFetch<CleanupOrphansResponse>(url, {
    method: 'GET',
  });
  return { data, status };
}

export async function deleteOrphanedDocs(): Promise<{
  data: CleanupOrphansResponse;
  status: number;
}> {
  const url = adminUrl('cleanupOrphanedDocs');
  const { data, status } = await adminFetch<CleanupOrphansResponse>(url, {
    method: 'DELETE',
  });
  return { data, status };
}

// ---------------------------------------------------------------------------
// getQueueStats (optional backend endpoint).
//
// If the backend exposes `/api/getQueueStats`, this returns queue depth and
// related metrics. The endpoint is OPTIONAL — when absent the call returns
// `null` and the dashboard falls back to a "not configured" state.
//
// Expected (suggested) response shape:
//   {
//     queues: [
//       { name: "partner-processing", approximateMessageCount: 42,
//         poisonMessageCount: 0, lastUpdated: "2026-04-29T..." },
//       ...
//     ],
//     functionApp?: { state: string, instanceCount?: number, region?: string },
//     timestamp: string
//   }
// ---------------------------------------------------------------------------

export interface QueueDepth {
  name: string;
  approximateMessageCount: number;
  poisonMessageCount?: number;
  lastUpdated?: string;
}

export interface QueueStatsResponse {
  queues: QueueDepth[];
  functionApp?: {
    state?: string;
    instanceCount?: number;
    region?: string;
    lastRestart?: string;
  };
  timestamp?: string;
}

/**
 * Fetch queue stats from the backend. Returns `null` if the endpoint is not
 * implemented (404) so callers can render a graceful "not available" state.
 * Other errors propagate.
 */
export async function getQueueStats(): Promise<QueueStatsResponse | null> {
  const url = adminUrl('getQueueStats');
  try {
    const { data } = await adminFetch<QueueStatsResponse>(url, { method: 'GET' });
    return data;
  } catch (e) {
    const err = e as Error & { status?: number };
    if (err.status === 404 || err.status === 405) return null;
    throw e;
  }
}

// ---------------------------------------------------------------------------
// scaleFunctionApp
//
// GET  -> read current site/plan/instance state
// POST -> change instance count (1-100) and/or SKU (B1/B2/B3/P0v3-P3v3);
//         returns same shape + scale result
// ---------------------------------------------------------------------------

/**
 * Allowed App Service Plan SKUs that can be set via `scaleFunctionApp`.
 *
 * The backend accepts an explicit allowlist that lets operators flip the
 * plan back to Premium v3 after the nightly Azure Policy reverts it to
 * Basic, or scale down to a smaller Basic tier for cost savings.
 */
export type ScaleFunctionAppSku =
  | 'B1' | 'B2' | 'B3'
  | 'P0v3' | 'P1v3' | 'P2v3' | 'P3v3';

export const SCALE_FUNCTION_APP_SKUS: ScaleFunctionAppSku[] = [
  'B1', 'B2', 'B3', 'P0v3', 'P1v3', 'P2v3', 'P3v3',
];

export type FunctionScaleProfileId = 'idle' | 'webiq' | 'nightly';

export interface FunctionScaleProfile {
  id: FunctionScaleProfileId;
  label: string;
  description: string;
  sku: ScaleFunctionAppSku;
  instances: number;
  alwaysOn: boolean;
}

/** Measured production profiles shared by the admin UI and operator guidance. */
export const FUNCTION_SCALE_PROFILES: Record<FunctionScaleProfileId, FunctionScaleProfile> = {
  idle: {
    id: 'idle',
    label: 'Idle baseline',
    description: 'Normal operation after queues drain.',
    sku: 'P0v3',
    instances: 1,
    alwaysOn: true,
  },
  webiq: {
    id: 'webiq',
    label: 'WebIQ discovery',
    description: 'I/O-bound source discovery with ten lower-cost workers.',
    sku: 'P0v3',
    instances: 10,
    alwaysOn: true,
  },
  nightly: {
    id: 'nightly',
    label: 'Nightly extraction',
    description: 'Latency-bound extraction with ten concurrent workers.',
    sku: 'P1v3',
    instances: 10,
    alwaysOn: true,
  },
};

export interface SkuSpec {
  sku: ScaleFunctionAppSku;
  tier: 'Basic' | 'PremiumV3';
  vCpu: number;
  memoryGb: number;
  /** Maximum worker count permitted by the SKU. */
  maxCapacity: number;
  /** Public retail price per instance per hour, USD. */
  costPerHour: number;
  /** Public retail price per instance per month, USD. */
  costPerMonth: number;
}

/**
 * Per-SKU specs. Hardware figures are from the App Service "Hardware view"
 * pricing pane; the rates come from `azurePricing`, which is the single source
 * for cost data so a page can price something without importing this client.
 */
export const SCALE_FUNCTION_APP_SKU_SPECS: Record<ScaleFunctionAppSku, SkuSpec> = {
  B1:   { sku: 'B1',   tier: 'Basic',     vCpu: 1, memoryGb: 1.75, maxCapacity: 3,  ...APP_SERVICE_SKU_RATES.B1 },
  B2:   { sku: 'B2',   tier: 'Basic',     vCpu: 2, memoryGb: 3.5,  maxCapacity: 3,  ...APP_SERVICE_SKU_RATES.B2 },
  B3:   { sku: 'B3',   tier: 'Basic',     vCpu: 4, memoryGb: 7,    maxCapacity: 3,  ...APP_SERVICE_SKU_RATES.B3 },
  P0v3: { sku: 'P0v3', tier: 'PremiumV3', vCpu: 1, memoryGb: 4,    maxCapacity: 30, ...APP_SERVICE_SKU_RATES.P0v3 },
  P1v3: { sku: 'P1v3', tier: 'PremiumV3', vCpu: 2, memoryGb: 8,    maxCapacity: 30, ...APP_SERVICE_SKU_RATES.P1v3 },
  P2v3: { sku: 'P2v3', tier: 'PremiumV3', vCpu: 4, memoryGb: 16,   maxCapacity: 30, ...APP_SERVICE_SKU_RATES.P2v3 },
  P3v3: { sku: 'P3v3', tier: 'PremiumV3', vCpu: 8, memoryGb: 32,   maxCapacity: 30, ...APP_SERVICE_SKU_RATES.P3v3 },
};

/** Normalize an arbitrary SKU string from ARM (e.g. "P2V3") to the allowlist key. */
export function normalizeSku(raw: string | undefined | null): ScaleFunctionAppSku | null {
  if (!raw) return null;
  const upper = raw.toUpperCase();
  for (const key of SCALE_FUNCTION_APP_SKUS) {
    if (key.toUpperCase() === upper) return key;
  }
  return null;
}

export interface ScaleFunctionAppSite {
  name?: string;
  state?: string;
  kind?: string;
  location?: string;
  defaultHostName?: string;
  enabled?: boolean;
  httpsOnly?: boolean;
  alwaysOn?: boolean;
  lastModifiedTimeUtc?: string;
  reserved?: boolean;
  linuxFxVersion?: string;
  numberOfWorkers?: number;
  serverFarmId?: string;
  [k: string]: any;
}

export interface ScaleFunctionAppPlan {
  resourceGroup?: string;
  name?: string;
  location?: string;
  kind?: string;
  status?: string;
  perSiteScaling?: boolean;
  maximumElasticWorkerCount?: number | null;
  numberOfWorkers?: number;
  numberOfSites?: number;
  capacity?: number;
  sku?: {
    name?: string;
    tier?: string;
    size?: string;
    family?: string;
    capacity?: number;
  };
  [k: string]: any;
}

export interface ScaleFunctionAppResponse {
  timestamp: string;
  request?: { targetInstances?: number; targetSku?: string; targetAlwaysOn?: boolean; alwaysOn?: boolean };
  result?: {
    success: boolean;
    previousCapacity?: number;
    newCapacity?: number;
    previousSku?: string;
    newSku?: string;
    previousAlwaysOn?: boolean;
    newAlwaysOn?: boolean;
  };
  warnings?: string[];
  scaleError?: {
    message?: string;
    code?: string;
    details?: any;
  };
  site?: ScaleFunctionAppSite;
  plan?: ScaleFunctionAppPlan;
  liveInstanceCount?: number;
  liveInstances?: Array<{ id?: string; name?: string; type?: string }>;
  error?: string;
  message?: string;
  allowedSkus?: string[];
}

/**
 * Read current Function App / plan / live instance state. Returns `null`
 * if the endpoint is not deployed yet (404/405) so the page can render a
 * graceful "not available" state.
 */
export async function getFunctionAppStatus(): Promise<ScaleFunctionAppResponse | null> {
  const url = adminUrl('scaleFunctionApp');
  try {
    const { data } = await adminFetch<ScaleFunctionAppResponse>(url, {
      method: 'GET',
    });
    return data;
  } catch (e) {
    const err = e as Error & { status?: number };
    if (err.status === 404 || err.status === 405) return null;
    throw e;
  }
}

/**
 * Scale the App Service Plan to the supplied target. Any combination of
 * `instances`, `sku`, and `alwaysOn` may be supplied. Returns the full
 * response body — including `scaleError` when ARM rejected the change.
 *
 * The backend returns 502 (with a populated body containing `scaleError`)
 * when ARM rejects the scale request; we surface that body to the caller
 * rather than throwing, so the UI can render the ARM error inline. A 400
 * (validation error from our function) is handled the same way.
 *
 * NOTE: The backend now defaults to enabling `alwaysOn=true` on every POST,
 * even when the body is empty — this is the recovery hook for the nightly
 * governance policy that disables AlwaysOn. Pass `alwaysOn: false`
 * explicitly to disable it.
 */
export async function scaleFunctionApp(
  target: { instances?: number; sku?: ScaleFunctionAppSku; alwaysOn?: boolean },
): Promise<{ data: ScaleFunctionAppResponse; status: number }> {
  const params: Record<string, string | number> = {};
  if (typeof target.instances === 'number') params.instances = target.instances;
  if (target.sku) params.sku = target.sku;
  if (typeof target.alwaysOn === 'boolean') params.alwaysOn = target.alwaysOn ? 'true' : 'false';
  const url = adminUrl('scaleFunctionApp', params);
  try {
    const { data, status } = await adminFetch<ScaleFunctionAppResponse>(url, {
      method: 'POST',
    });
    return { data, status };
  } catch (e) {
    const err = e as Error & { status?: number; body?: any };
    // 502 = scale rejected by ARM but body still contains site/plan/scaleError.
    // 400 = validation error from our function with body.message.
    if ((err.status === 502 || err.status === 400) && err.body) {
      return { data: err.body as ScaleFunctionAppResponse, status: err.status };
    }
    throw e;
  }
}

// ---------------------------------------------------------------------------
// infrastructure
// ---------------------------------------------------------------------------

export interface InfrastructureStatusResponse {
  timestamp: string;
  subscriptionId?: string;
  resourceGroup?: string;
  error?: string;
  message?: string;
  components: {
    sqlDatabase?: {
      name?: string;
      serverName?: string;
      edition?: string | null;
      sku?: { name?: string | null; tier?: string | null; size?: string | null; family?: string | null; capacity?: number | null } | null;
      vCores?: number | null;
      status?: string | null;
      serviceLevelObjective?: string | null;
      maxSizeBytes?: number | null;
      location?: string | null;
      error?: string;
    };
    functionAppPlan?: {
      name?: string;
      functionAppName?: string;
      sku?: { name?: string | null; tier?: string | null; size?: string | null; family?: string | null; capacity?: number | null } | null;
      workerCount?: number | null;
      alwaysOn?: boolean | null;
      status?: string | null;
      location?: string | null;
      maximumElasticWorkerCount?: number | null;
      error?: string;
      alwaysOnError?: string;
    };
    readerApi?: {
      name?: string;
      minReplicas?: number | null;
      maxReplicas?: number | null;
      currentReplicas?: number | null;
      activeRevisions?: string[];
      provisioningState?: string | null;
      runningStatus?: string | null;
      latestReadyRevisionName?: string | null;
      location?: string | null;
      error?: string;
    };
    nightlyJob?: {
      name?: string;
      cronSchedule?: string | null;
      cpu?: number | null;
      memory?: string | null;
      replicaTimeoutSeconds?: number | null;
      replicaRetryLimit?: number | null;
      parallelism?: number | null;
      replicaCompletionCount?: number | null;
      provisioningState?: string | null;
      lastExecution?: {
        name?: string | null;
        status?: string | null;
        startTime?: string | null;
        endTime?: string | null;
      } | null;
      location?: string | null;
      error?: string;
    };
    webIqJob?: {
      name?: string;
      cronSchedule?: string | null;
      cpu?: number | null;
      memory?: string | null;
      replicaTimeoutSeconds?: number | null;
      replicaRetryLimit?: number | null;
      parallelism?: number | null;
      replicaCompletionCount?: number | null;
      provisioningState?: string | null;
      lastExecution?: {
        name?: string | null;
        status?: string | null;
        startTime?: string | null;
        endTime?: string | null;
      } | null;
      location?: string | null;
      error?: string;
      statusSource?: string;
      armReadError?: string;
    };
  };
}

export async function getInfrastructureStatus(): Promise<InfrastructureStatusResponse | null> {
  const url = adminUrl('infrastructure');
  try {
    const { data } = await adminFetch<InfrastructureStatusResponse>(url, { method: 'GET' });
    return data;
  } catch (e) {
    const err = e as Error & { status?: number };
    if (err.status === 404 || err.status === 405) return null;
    throw e;
  }
}

/** vCore counts Azure SQL General Purpose Gen5 accepts. Mirrors the backend allowlist. */
export const ALLOWED_SQL_VCORES = [2, 4, 6, 8, 10, 12, 14, 16, 18, 20, 24, 32, 40] as const;

/** Matches the Reader ceiling the scaled Function App can actually drive. */
export const MAX_READER_REPLICAS = 10;

/** vCores the nightly run scales SQL between. Re-exported from the pricing module. */
export { SQL_NIGHTLY_VCORES, SQL_IDLE_VCORES } from './azurePricing';

export type ScaleInfraTarget = 'sql' | 'reader';

export interface ScaleInfrastructureRequest {
  target: ScaleInfraTarget;
  vCores?: number;
  minReplicas?: number;
  maxReplicas?: number;
}

export interface ScaleInfrastructureResponse extends InfrastructureStatusResponse {
  request?: {
    target?: ScaleInfraTarget;
    vCores?: number;
    minReplicas?: number;
    maxReplicas?: number;
  };
  result?: {
    success?: boolean;
    accepted?: boolean;
    changed?: boolean;
    previousVCores?: number | null;
    newVCores?: number | null;
    sku?: string | null;
    previous?: { minReplicas?: number | null; maxReplicas?: number | null };
    next?: { minReplicas?: number | null; maxReplicas?: number | null };
  };
  scaleError?: { message?: string; code?: string | number | null; details?: unknown };
  allowedSqlVCores?: number[];
  maxReaderReplicas?: number;
}

/**
 * Manually resize SQL or the Reader.
 *
 * ARM applies both changes asynchronously -- a SQL resize in particular can
 * take minutes -- so a successful response means the request was accepted, not
 * that the resource has finished resizing. The caller should keep polling
 * `getInfrastructureStatus`.
 */
export async function scaleInfrastructure(
  request: ScaleInfrastructureRequest,
): Promise<ScaleInfrastructureResponse | null> {
  const url = adminUrl('infrastructure');
  try {
    const { data } = await adminFetch<ScaleInfrastructureResponse>(url, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(request),
    });
    return data;
  } catch (e) {
    const err = e as Error & { status?: number };
    if (err.status === 404 || err.status === 405) return null;
    throw e;
  }
}

// ---------------------------------------------------------------------------
// eventInfoDuplicates
//
// GET    ?action=count               -> aggregate counts (default)
// GET    ?action=list&page=&limit=   -> paginated list of duplicate records
// DELETE ?action=duplicates&confirm=true -> collapse duplicate groups
// DELETE ?action=cleanup&confirm=true    -> drop too-old / too-future events
// ---------------------------------------------------------------------------

export interface DuplicateCountResponse {
  action: 'count';
  duplicateGroups: number;
  totalDuplicateRecords: number;
  summary: {
    averageDuplicatesPerGroup: number;
    maxDuplicatesInSingleGroup: number;
  };
}

export interface DuplicateMapRecord {
  eventSourceId?: number;
  matchScore?: number;
  entityEventTitle?: string;
  entityEventUniqueId?: string;
  screenshotProcessId?: number;
  [k: string]: any;
}

export interface DuplicateRecord {
  eventInfoId: number;
  eventTitle?: string;
  startDate?: string;
  endDate?: string;
  type?: string;
  venue?: string;
  city?: string;
  state?: string;
  country?: string;
  isVirtual?: boolean;
  eventUrl?: string;
  dateCreated?: string;
  dateModified?: string;
  cleanUpMessage?: string | null;
  duplicateRank?: number;
  eventInfoMapRecords?: DuplicateMapRecord[];
  [k: string]: any;
}

export interface DuplicateListResponse {
  action: 'list';
  pagination: {
    page: number;
    limit: number;
    totalItems: number;
    totalPages: number;
    hasNextPage: boolean;
    hasPreviousPage: boolean;
  };
  records: DuplicateRecord[];
}

export interface DuplicateDeleteResponse {
  action: 'delete' | string;
  summary?: string;
  deletedEventInfoRecords?: number;
  deletedEventInfoMapRecords?: number;
  duplicateGroupsProcessed?: number;
  preservedRecords?: number;
  searchIndexCleanup?: {
    success: boolean;
    deletedSearchDocuments: number;
    message?: string;
  };
  [k: string]: any;
}

export async function getDuplicateCount(): Promise<DuplicateCountResponse> {
  const url = adminUrl('eventInfoDuplicates', { action: 'count' });
  const { data } = await adminFetch<DuplicateCountResponse>(url, { method: 'GET' });
  return data;
}

export async function listDuplicates(
  page: number = 1,
  limit: number = 50,
): Promise<DuplicateListResponse> {
  const url = adminUrl('eventInfoDuplicates', { action: 'list', page, limit });
  const { data } = await adminFetch<DuplicateListResponse>(url, { method: 'GET' });
  return data;
}

export async function deleteDuplicates(): Promise<DuplicateDeleteResponse> {
  const url = adminUrl('eventInfoDuplicates', {
    action: 'duplicates',
    confirm: 'true',
  });
  const { data } = await adminFetch<DuplicateDeleteResponse>(url, {
    method: 'DELETE',
  });
  return data;
}

export async function cleanupStaleEvents(): Promise<DuplicateDeleteResponse> {
  const url = adminUrl('eventInfoDuplicates', {
    action: 'cleanup',
    confirm: 'true',
  });
  const { data } = await adminFetch<DuplicateDeleteResponse>(url, {
    method: 'DELETE',
  });
  return data;
}

// ---------------------------------------------------------------------------
// workflowMetadata
//
// GET    ?action=summary           (default; aggregate stats over time window)
// GET    ?action=lookup            (rows by eventSourceId or source substring)
// GET    ?action=orphan-entities   (entities with no EntityEventSources)
// DELETE ?action=delete-source&eventSourceId=&confirm=true
// DELETE ?action=delete-failed&confirm=true   (latest-run failures in window)
// DELETE ?action=delete-orphan-entities&confirm=true
// ---------------------------------------------------------------------------

export interface WorkflowWindow {
  startTime?: string;
  endTime?: string;
}

export interface WorkflowSummaryResponse {
  action: 'summary';
  window: WorkflowWindow;
  totalCount: number;
  statusCounts: Array<{ status: string; count: number }>;
  stageCounts: Array<{ stage: string; count: number }>;
  failureStageCounts: Array<{ stage: string; count: number }>;
  failureDetection: { patterns: string[] };
  eventCoverage: {
    perRunAverageRatio: number;
    overallRatio: number;
    sumTotalNewEvents: number;
    sumTotalEvents: number;
    runsConsidered: number;
  };
  orphanEntityCount: number;
}

export interface WorkflowLookupRecord {
  Id: number;
  EventSourceId: number;
  Source?: string;
  EventsScreenshot?: string;
  InstanceId?: string;
  StartTime?: string;
  EndTime?: string;
  Status?: string;
  TotalEvents?: number | null;
  TotalNewEvents?: number | null;
  Stage?: string;
  EventSourceUrl?: string;
  EventSourceInfo?: string;
  EventSourceType?: string;
  [k: string]: any;
}

export interface WorkflowLookupResponse {
  action: 'lookup';
  criteria: {
    eventSourceId?: number | null;
    source?: string | null;
    window?: WorkflowWindow | null;
  };
  count: number;
  records: WorkflowLookupRecord[];
}

export interface WorkflowOrphanEntity {
  Id: number;
  EntityName?: string;
  EntityTypeId?: number;
  DateCreated?: string;
  OneId?: string | null;
  EntityContact?: string | null;
  [k: string]: any;
}

export interface WorkflowOrphanEntitiesResponse {
  action: 'orphan-entities';
  count: number;
  entities: WorkflowOrphanEntity[];
}

export interface WorkflowDeletedTotals {
  workflowMetadata?: number;
  entityEventSources?: number;
  eventInfoMap?: number;
  eventSources?: number;
}

export interface WorkflowDeleteSourceResponse {
  action: 'delete-source';
  eventSourceId: number;
  sourceUrl?: string;
  found: boolean;
  message?: string;
  deleted?: WorkflowDeletedTotals;
}

export interface WorkflowDeleteFailedResponse {
  action: 'delete-failed';
  window: WorkflowWindow;
  failureDetection: { patterns: string[] };
  candidatesFound: number;
  deletedTotals: WorkflowDeletedTotals;
  deleted: Array<{
    eventSourceId: number;
    lastStatus?: string;
    lastStage?: string;
    lastStartTime?: string;
  }>;
}

export interface WorkflowDeleteOrphanEntitiesResponse {
  action: 'delete-orphan-entities';
  deletedCount: number;
  deleted: Array<{ Id: number; EntityName?: string }>;
}

export interface WorkflowSummaryParams {
  startTime?: string;
  endTime?: string;
  failurePattern?: string;
}

export async function getWorkflowSummary(
  params: WorkflowSummaryParams = {},
): Promise<WorkflowSummaryResponse> {
  const url = adminUrl('workflowMetadata', {
    action: 'summary',
    startTime: params.startTime,
    endTime: params.endTime,
    failurePattern: params.failurePattern,
  });
  const { data } = await adminFetch<WorkflowSummaryResponse>(url, {
    method: 'GET',
  });
  return data;
}

export interface WorkflowLookupParams {
  eventSourceId?: number;
  source?: string;
  allTime?: boolean;
  startTime?: string;
  endTime?: string;
}

export async function lookupWorkflowRuns(
  params: WorkflowLookupParams,
): Promise<WorkflowLookupResponse> {
  const url = adminUrl('workflowMetadata', {
    action: 'lookup',
    eventSourceId: params.eventSourceId,
    source: params.source,
    allTime: params.allTime ? 'true' : undefined,
    startTime: params.allTime ? undefined : params.startTime,
    endTime: params.allTime ? undefined : params.endTime,
  });
  const { data } = await adminFetch<WorkflowLookupResponse>(url, {
    method: 'GET',
  });
  return data;
}

export async function getOrphanEntities(): Promise<WorkflowOrphanEntitiesResponse> {
  const url = adminUrl('workflowMetadata', { action: 'orphan-entities' });
  const { data } = await adminFetch<WorkflowOrphanEntitiesResponse>(url, {
    method: 'GET',
  });
  return data;
}

export async function deleteEventSourceCascade(
  eventSourceId: number,
): Promise<WorkflowDeleteSourceResponse> {
  const url = adminUrl('workflowMetadata', {
    action: 'delete-source',
    eventSourceId,
    confirm: 'true',
  });
  const { data } = await adminFetch<WorkflowDeleteSourceResponse>(url, {
    method: 'DELETE',
  });
  return data;
}

export interface DeleteFailedParams {
  startTime?: string;
  endTime?: string;
  failurePattern?: string;
}

export async function deleteFailedSources(
  params: DeleteFailedParams = {},
): Promise<WorkflowDeleteFailedResponse> {
  const url = adminUrl('workflowMetadata', {
    action: 'delete-failed',
    confirm: 'true',
    startTime: params.startTime,
    endTime: params.endTime,
    failurePattern: params.failurePattern,
  });
  const { data } = await adminFetch<WorkflowDeleteFailedResponse>(url, {
    method: 'DELETE',
  });
  return data;
}

export async function deleteOrphanEntities(): Promise<WorkflowDeleteOrphanEntitiesResponse> {
  const url = adminUrl('workflowMetadata', {
    action: 'delete-orphan-entities',
    confirm: 'true',
  });
  const { data } = await adminFetch<WorkflowDeleteOrphanEntitiesResponse>(url, {
    method: 'DELETE',
  });
  return data;
}

// ---------------------------------------------------------------------------
// workflowMetadata ?action=runs
//
// GET    ?action=runs&startTime=&endTime=&page=&pageSize=&status=
//   Returns paginated processing run history with full stats per run,
//   plus aggregate summary totals and estimated LLM costs.
// ---------------------------------------------------------------------------

export interface ProcessingRun {
  id: number;
  eventSourceId: number;
  source?: string;
  screenshotUrl?: string;
  startTime?: string;
  endTime?: string;
  status?: string;
  stage?: string;
  entityId?: number | null;
  partnerName?: string | null;
  // Event counts
  totalEventsFound?: number | null;
  totalNewEvents?: number | null;
  exactDuplicates?: number | null;
  vectorMatches?: number | null;
  totalErrors?: number | null;
  // Processing info
  extractionMethod?: string | null;
  screenshotsCount?: number | null;
  processingDurationSec?: number | null;
  // Token counts (null for old runs)
  promptTokens?: number | null;
  completionTokens?: number | null;
  totalTokens?: number | null;
  embeddingTokens?: number | null;
  // Cost estimate (null for old runs)
  estimatedCostUsd?: number | null;
}

export interface ProcessingRunsSummary {
  totalRuns: number;
  successfulRuns: number;
  failedRuns: number;
  totalEventsFound: number;
  totalNewEvents: number;
  totalExactDuplicates: number;
  totalVectorMatches: number;
  totalScreenshots: number;
  totalTokens: number;
  totalEmbeddingTokens: number;
  totalEstimatedCostUsd: number;
  avgDurationSec: number | null;
  maxDurationSec: number | null;
  earliestRun: string | null;
  latestRun: string | null;
}

export interface PricingInfoModel {
  inputPerMillion: number;
  outputPerMillion: number;
  unit: string;
}

export interface ProcessingRunsResponse {
  page: number;
  pageSize: number;
  totalCount: number;
  totalPages: number;
  timeRange: { startTime: string; endTime: string };
  summary: ProcessingRunsSummary;
  pricingInfo: {
    note: string;
    priceUrl: string;
    models: Record<string, PricingInfoModel>;
  };
  runs: ProcessingRun[];
}

export interface GetProcessingRunsParams {
  startTime?: string;
  endTime?: string;
  page?: number;
  pageSize?: number;
  status?: string;
  entityId?: number;
}

export async function getProcessingRuns(
  params: GetProcessingRunsParams = {},
): Promise<ProcessingRunsResponse> {
  const url = adminUrl('workflowMetadata', {
    action: 'runs',
    startTime: params.startTime,
    endTime: params.endTime,
    page: params.page,
    pageSize: params.pageSize,
    status: params.status,
    entityId: params.entityId,
  });
  const { data } = await adminFetch<ProcessingRunsResponse>(url, { method: 'GET' });
  return data;
}

// ---------------------------------------------------------------------------
// nightly-runs
// ---------------------------------------------------------------------------

export interface NightlyRun {
  runId: string;
  startedAt: string;
  completedAt?: string | null;
  durationSeconds?: number | null;
  status: 'running' | 'succeeded' | 'failed' | 'deadline-exceeded' | string;
  workload: 'extract' | 'webiq' | string;
  entityTypes: string[];
  entityLabel?: string | null;
  runLabel?: string | null;
  nightName?: string | null;
  nightDate?: string | null;
  utcDayName?: string | null;
  dryRun: boolean;
  hardDeadlineMinutes?: number | null;
  exitCode?: number | null;
  failures?: unknown;
  scaleUpSku?: string | null;
  scaleUpInstances?: number | null;
  sqlUpVCores?: number | null;
  scaleDownVerified?: boolean | null;
  sqlScaleDownVerified?: boolean | null;
  readerReplicasAtEnd?: number | null;
  enqueuedCount?: number | null;
  webIqStats?: Record<string, any> | null;
  createdAt?: string | null;
}

export interface NightlyRunSample {
  id: number;
  runId: string;
  sampledAt: string;
  /**
   * Which stage of the run produced this snapshot. Null on runs recorded before
   * phase tagging shipped, when only the drain loop emitted samples.
   */
  phase?: string | null;
  elapsedSeconds?: number | null;
  queueDepth?: number | null;
  queueDepths?: Record<string, number | null> | null;
  poisonCount?: number | null;
  functionInstanceCount?: number | null;
  functionCpuPercent?: number | null;
  functionMemoryMb?: number | null;
  functionSku?: string | null;
  sqlVCores?: number | null;
  sqlCpuPercent?: number | null;
  sqlDtuPercent?: number | null;
  readerReplicas?: number | null;
  readerCpuPercent?: number | null;
  readerMemoryMb?: number | null;
  notes?: string | null;
}

export interface NightlyRunsResponse {
  page: number;
  pageSize: number;
  totalCount: number;
  totalPages: number;
  runs: NightlyRun[];
}

export interface NightlyRunDetailResponse {
  run: NightlyRun;
  samples: NightlyRunSample[];
}

export interface GetNightlyRunsParams {
  startTime?: string;
  endTime?: string;
  page?: number;
  pageSize?: number;
  status?: string;
  workload?: string;
}

export async function getNightlyRuns(
  params: GetNightlyRunsParams = {},
): Promise<NightlyRunsResponse> {
  const { data } = await adminFetch<NightlyRunsResponse>(
    adminUrl('nightly-runs', params as Record<string, unknown>),
    { method: 'GET' },
  );
  return data;
}

export async function getNightlyRun(runId: string): Promise<NightlyRunDetailResponse> {
  const { data } = await adminFetch<NightlyRunDetailResponse>(
    adminUrl('nightly-runs', { runId }),
    { method: 'GET' },
  );
  return data;
}

export interface NightlyScheduleNight {  triggerAt: string;
  utcDayName: string;
  nightName: string;
  nightDate: string;
  workload: 'extract' | 'webiq' | string;
  entityTypes: string[];
  entityLabel: string;
  jobName: string;
  runnerName?: string;
  runLabel: string;
  previousRuns: NightlyRun[];
}

export interface NightlyScheduleResponse {
  generatedAt: string;
  triggerHourUtc: number;
  schedules?: Array<{ runnerName: string; cronSchedule: string }>;
  nights: NightlyScheduleNight[];
}

export async function getNightlySchedule(
  params: { nights?: number; history?: number } = {},
): Promise<NightlyScheduleResponse> {
  const { data } = await adminFetch<NightlyScheduleResponse>(
    adminUrl('nightly-runs', { view: 'schedule', ...params }),
    { method: 'GET' },
  );
  return data;
}

// ---------------------------------------------------------------------------
// source-cleanup
//
// GET  previews the weekly sweep without changing anything.
// POST executes it. Both return the same report shape.
// ---------------------------------------------------------------------------

export interface RetiredSource {
  eventSourceId: number;
  source: string | null;
  consecutiveFailures: number;
  lastSuccessfulExtractionAt?: string | null;
  lastStatus?: string | null;
  lastStage?: string | null;
  lastRunAt?: string | null;
}

export interface DefectiveSource {
  eventSourceId: number;
  source: string | null;
  reasons: string[];
  detail: string;
  lastSuccessfulExtractionAt?: string | null;
  eventsExtractedCount: number;
}

export interface FlaggedSource {
  eventSourceId: number;
  source: string | null;
  lastSuccessfulExtractionAt?: string | null;
  eventsExtractedCount: number;
  upcomingCoverageCount?: number | null;
  observations?: string[];
}

export interface SourceCleanupReport {
  startedAt: string;
  completedAt?: string | null;
  policy: {
    minConsecutiveFailures: number;
    successGraceDays: number;
    quietDays: number;
    maxRetirePerRun: number;
    outageRatio: number;
    dryRun: boolean;
  };
  activeSources: number;
  candidatesFound: number;
  failingCount: number;
  defectiveCount: number;
  defectCounts: Record<string, number>;
  retiredCount: number;
  mappingsDeactivated: number;
  skipped?: { reason: string; detail: string; threshold?: number } | null;
  retired: RetiredSource[];
  defective: DefectiveSource[];
  flaggedForReview: {
    reason: string;
    quietDays: number;
    count: number;
    sources: FlaggedSource[];
  };
}

export interface SourceCleanupParams {
  minFailures?: number;
  graceDays?: number;
  quietDays?: number;
  maxRetire?: number;
}

export async function previewSourceCleanup(
  params: SourceCleanupParams = {},
): Promise<SourceCleanupReport> {
  const { data } = await adminFetch<SourceCleanupReport>(
    adminUrl('source-cleanup', params as Record<string, unknown>),
    { method: 'GET' },
  );
  return data;
}

export async function runSourceCleanup(
  params: SourceCleanupParams = {},
): Promise<SourceCleanupReport> {
  const { data } = await adminFetch<SourceCleanupReport>(
    adminUrl('source-cleanup', params as Record<string, unknown>),
    { method: 'POST' },
  );
  return data;
}

// ---------------------------------------------------------------------------
// workflowMetadata ?action=active
//
// GET    ?action=active
//   Returns all runs that are currently in progress (no EndTime within 2h window).
//   Intended for live polling on the admin dashboard.
// ---------------------------------------------------------------------------

export interface ActiveRun {
  id: number;
  eventSourceId: number | null;
  source?: string | null;
  instanceId?: string | null;
  startTime?: string;
  endTime?: string | null;
  status?: string | null;
  stage?: string | null;
  partnerName?: string | null;
  entityName?: string | null;
  totalEventsFound?: number | null;
  totalNewEvents?: number | null;
  exactDuplicates?: number | null;
  vectorMatches?: number | null;
  totalErrors?: number | null;
  extractionMethod?: string | null;
  screenshotsCount?: number | null;
  promptTokens?: number | null;
  completionTokens?: number | null;
  totalTokens?: number | null;
  embeddingTokens?: number | null;
  estimatedCostUsd?: number | null;
}

export interface ActiveRunsResponse {
  action: string;
  count: number;
  runs: ActiveRun[];
}

export async function getActiveRuns(): Promise<ActiveRunsResponse> {
  const url = adminUrl('workflowMetadata', { action: 'active' });
  const { data } = await adminFetch<ActiveRunsResponse>(url, { method: 'GET' });
  return data;
}

// ---------------------------------------------------------------------------
// wipeEvents
//
// GET    -> dry run (counts only, never deletes)
// DELETE -> perform wipe of EventInfo / EventInfoMap / EventTitleAlias rows
//           created after `createdAfter`, plus matching search-index docs.
// ---------------------------------------------------------------------------

export interface WipeEventsCounts {
  eventInfo?: number;
  eventInfoMap?: number;
  eventTitleAlias?: number;
  searchDocs?: number;
}

/**
 * Echoed filter block. The backend returns the supplied param value plus, for
 * relative day-based filters, a resolved `<param>_cutoff` ISO timestamp so
 * callers can verify the day arithmetic.
 */
export interface WipeEventsFiltersEcho {
  startDateOlderThanDays?: number;
  startDateOlderThanDays_cutoff?: string;
  createdOlderThanDays?: number;
  createdOlderThanDays_cutoff?: string;
  createdNewerThanDays?: number;
  createdNewerThanDays_cutoff?: string;
  startDateBefore?: string;
  createdBefore?: string;
  createdAfter?: string;
  [k: string]: unknown;
}

export interface WipeEventsDryRunResponse {
  dryRun: true;
  /** New: structured echo of resolved filters. */
  filters?: WipeEventsFiltersEcho;
  /** New: true when no filters were supplied and the safe default was applied. */
  defaultsApplied?: boolean;
  /** New: literal SQL fragment that will be applied (parameters are bound). */
  whereClause?: string;
  /** Legacy: present only when the older `createdAfter`-only path was used. */
  cutoffDate?: string;
  filter?: string;
  wouldDelete: WipeEventsCounts;
  searchPreviewError?: string;
  note?: string;
  message?: string;
}

export interface WipeEventsDeleteResponse {
  dryRun: false;
  filters?: WipeEventsFiltersEcho;
  defaultsApplied?: boolean;
  whereClause?: string;
  cutoffDate?: string;
  deleted: WipeEventsCounts;
  searchIndex?: {
    matched?: number;
    deleted?: number;
    failed?: number;
    errors?: string[];
  };
  message?: string;
}

export type WipeEventsResponse = WipeEventsDryRunResponse | WipeEventsDeleteResponse;

/**
 * All filters are optional and AND-combined server-side. If none are supplied
 * the API applies the safe default `startDateOlderThanDays=90`.
 */
export interface WipeEventsParams {
  /** Wipe events whose StartDate is older than X days. Server default (when no filters at all) is 90. */
  startDateOlderThanDays?: number;
  /** Wipe events whose DateCreated is older than X days. */
  createdOlderThanDays?: number;
  /** Wipe events whose DateCreated is newer than X days (rollback last import). */
  createdNewerThanDays?: number;
  /** Absolute cutoff: StartDate < startDateBefore. ISO date or timestamp. */
  startDateBefore?: string;
  /** Absolute cutoff: DateCreated < createdBefore. ISO date or timestamp. */
  createdBefore?: string;
  /** Legacy absolute cutoff: DateCreated > createdAfter. ISO date or timestamp. */
  createdAfter?: string;
}

function wipeQuery(params: WipeEventsParams): Record<string, unknown> {
  return {
    startDateOlderThanDays: params.startDateOlderThanDays,
    createdOlderThanDays: params.createdOlderThanDays,
    createdNewerThanDays: params.createdNewerThanDays,
    startDateBefore: params.startDateBefore,
    createdBefore: params.createdBefore,
    createdAfter: params.createdAfter,
  };
}

export async function previewWipeEvents(
  params: WipeEventsParams = {},
): Promise<WipeEventsDryRunResponse> {
  const url = adminUrl('wipeEvents', wipeQuery(params));
  const { data } = await adminFetch<WipeEventsDryRunResponse>(url, {
    method: 'GET',
  });
  return data;
}

export async function wipeEvents(
  params: WipeEventsParams = {},
): Promise<WipeEventsDeleteResponse> {
  const url = adminUrl('wipeEvents', wipeQuery(params));
  const { data } = await adminFetch<WipeEventsDeleteResponse>(url, {
    method: 'DELETE',
  });
  return data;
}

// ---------------------------------------------------------------------------
// rebuildCache + getAppMetadata
//
// The cache layer pre-computes the most popular search results into Azure
// Blob Storage. The admin endpoints are:
//   POST /api/rebuildCache?dryRun=&quick=  -> 202 + { jobId, statusUrl, ... }
//                                            (or inline result when quick=true)
//   GET  /api/rebuildCache                 -> latest meta/manifest.json
//   GET  /api/getAppMetadata               -> meta/app.json snapshot
// ---------------------------------------------------------------------------

export interface CacheSectionResult {
  count?: number;
  durationMs?: number;
  errors?: Array<{ message: string; [k: string]: any } | string>;
  skipped?: boolean;
  [k: string]: any;
}

export interface CacheManifest {
  startedAt?: string;
  completedAt?: string;
  durationMs?: number;
  dryRun?: boolean;
  mode?: string;
  totals?: {
    cities?: number;
    categoriesVirtual?: number;
    partners?: number;
    companies?: number;
    [k: string]: any;
  };
  sections?: {
    cities?: CacheSectionResult;
    categoriesVirtual?: CacheSectionResult;
    partners?: CacheSectionResult;
    companies?: CacheSectionResult;
    appMetadata?: CacheSectionResult;
    [k: string]: CacheSectionResult | undefined;
  };
  errors?: Array<{ section?: string; message: string; [k: string]: any } | string>;
  jobId?: string;
  status?: string;
  message?: string;
  [k: string]: any;
}

export interface RebuildCacheTriggerResponse {
  /** Returned for queued runs (default async path). */
  jobId?: string;
  /** "queued" | "inline" | "dryRun" | etc. */
  mode?: string;
  manifestKey?: string;
  /** Relative URL of the latest manifest endpoint. */
  statusUrl?: string;
  message?: string;
  /** When `quick=true` the backend may return the inline manifest body. */
  manifest?: CacheManifest;
  [k: string]: any;
}

export interface TriggerCacheRebuildParams {
  dryRun?: boolean;
  /** Skip the queue and run inline. Only safe with dryRun=true. */
  quick?: boolean;
}

export async function triggerCacheRebuild(
  params: TriggerCacheRebuildParams = {},
): Promise<{ data: RebuildCacheTriggerResponse; status: number }> {
  const url = adminUrl('rebuildCache', {
    dryRun: params.dryRun ? 'true' : undefined,
    quick: params.quick ? 'true' : undefined,
  });
  const { data, status } = await adminFetch<RebuildCacheTriggerResponse>(url, {
    method: 'POST',
  });
  return { data, status };
}

/**
 * Fetch the latest cache build manifest. Returns `null` when no build has
 * ever been written (backend returns 404).
 */
export async function getCacheManifest(): Promise<CacheManifest | null> {
  const url = adminUrl('rebuildCache');
  try {
    const { data } = await adminFetch<CacheManifest>(url, { method: 'GET' });
    return data;
  } catch (e) {
    const err = e as Error & { status?: number };
    if (err.status === 404) return null;
    throw e;
  }
}

/**
 * Fetch the prebuilt app metadata snapshot. Returns `null` when the cache
 * has not been built yet (backend returns 503).
 */
export async function getAppMetadata(): Promise<AppMetadata | null> {
  const url = adminUrl('getAppMetadata');
  try {
    const { data } = await adminFetch<AppMetadata>(url, { method: 'GET' });
    return data;
  } catch (e) {
    const err = e as Error & { status?: number };
    if (err.status === 503 || err.status === 404) return null;
    throw e;
  }
}

// ---------------------------------------------------------------------------
// getCacheStats — cache hit/miss telemetry from App Insights / KQL.
//
// Query parameters (all optional):
//   - since      ISO 8601 duration (PT1H, P7D, …). Default: last 24h.
//   - gate       'city' | 'categoryVirtual' | 'entity' to scope to one gate.
//   - groupBy    Comma-separated list of grouping fields. Common values:
//                  reason, city, state, iso2, categoryName, entityId.
//                Default: gate + hit + reason.
//   - top        Numeric cap on returned rows.
//   - raw        'true' to return the raw KQL columns + rows.
//
// The endpoint is OPTIONAL — when absent the call returns `null`.
// ---------------------------------------------------------------------------

export type CacheStatsGate = 'city' | 'categoryVirtual' | 'entity';

export interface CacheStatsParams {
  since?: string;
  gate?: CacheStatsGate;
  groupBy?: string | string[];
  top?: number;
  raw?: boolean;
}

export interface CacheStatsRow {
  /** Group keys vary by `groupBy`; common ones include: */
  gate?: string;
  hit?: boolean;
  reason?: string;
  city?: string;
  state?: string;
  iso2?: string;
  categoryName?: string;
  entityId?: number | string;
  /** Aggregate count for this group. */
  count?: number;
  hits?: number;
  misses?: number;
  [k: string]: unknown;
}

export interface CacheStatsSummary {
  total?: number;
  hits?: number;
  misses?: number;
  hitRate?: number;
  byGate?: Array<{
    gate: string;
    total?: number;
    hits?: number;
    misses?: number;
    hitRate?: number;
  }>;
  [k: string]: unknown;
}

export interface CacheStatsResponse {
  /** ISO 8601 duration the query was executed against. */
  since?: string;
  /** Resolved start/end timestamps from the backend. */
  window?: { startTime?: string; endTime?: string };
  gate?: string;
  groupBy?: string[];
  top?: number;
  summary?: CacheStatsSummary;
  rows?: CacheStatsRow[];
  /** Present only when raw=true. */
  columns?: string[];
  rawRows?: unknown[][];
  /** Echoed for debugging when the backend exposes it. */
  query?: string;
  error?: string;
  message?: string;
  [k: string]: unknown;
}

export interface CacheStatsError extends Error {
  status?: number;
  body?: any;
}

/**
 * Fetch cache hit/miss telemetry. Returns `null` if the endpoint is not
 * deployed (404/405) so the page can render a graceful fallback.
 *
 * 400-range validation errors (bad `since`, unknown `groupBy` field) are
 * thrown so callers can surface the message inline.
 */
export async function getCacheStats(
  params: CacheStatsParams = {},
): Promise<CacheStatsResponse | null> {
  const groupBy = Array.isArray(params.groupBy)
    ? params.groupBy.filter(Boolean).join(',')
    : params.groupBy;
  const url = adminUrl('getCacheStats', {
    since: params.since,
    gate: params.gate,
    groupBy: groupBy || undefined,
    top: params.top,
    raw: params.raw ? 'true' : undefined,
  });
  try {
    const { data } = await adminFetch<CacheStatsResponse>(url, { method: 'GET' });
    return data;
  } catch (e) {
    const err = e as CacheStatsError;
    if (err.status === 404 || err.status === 405) return null;
    throw e;
  }
}

// ---------------------------------------------------------------------------
// "Last run" persistence helper (per UX spec).
// Stored in localStorage so each admin page can show a "Last run" card.
// ---------------------------------------------------------------------------

export interface LastRunRecord<T = unknown> {
  timestamp: string;
  triggeredBy?: string;
  durationMs?: number;
  summary?: Record<string, unknown>;
  response?: T;
}

const LAST_RUN_PREFIX = 'admin.lastRun.';

export function saveLastRun<T>(action: string, record: LastRunRecord<T>): void {
  try {
    localStorage.setItem(LAST_RUN_PREFIX + action, JSON.stringify(record));
  } catch {
    // ignore — quota or private mode
  }
}

export function loadLastRun<T>(action: string): LastRunRecord<T> | null {
  try {
    const raw = localStorage.getItem(LAST_RUN_PREFIX + action);
    return raw ? (JSON.parse(raw) as LastRunRecord<T>) : null;
  } catch {
    return null;
  }
}
